import React from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";

interface CoachKpiCardProps {
  icon: React.ElementType;
  label: string;
  value: number | string;
  hint?: string;
  trend?: { diff: number };
  data?: { date: string; value: number }[];
  index?: number;
  compact?: boolean;
}

export function CoachKpiCard({
  icon: Icon,
  label,
  value,
  hint,
  trend,
  data,
  index = 0,
  compact = false,
}: CoachKpiCardProps) {
  const isUp = (trend?.diff ?? 0) >= 0;

  return (
    <div
      className={cn("coach-kpi-card", compact ? "coach-kpi-card--dashboard p-3 md:p-4" : "p-4")}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className={cn("flex justify-between", compact ? "items-start" : "items-center")}>
        <div className={cn("flex min-w-0", compact ? "flex-col items-start gap-2" : "items-center gap-2")}>
          <span className={cn("coach-icon-box inline-flex items-center justify-center rounded-md text-primary shrink-0", compact ? "h-10 w-10" : "h-9 w-9")}>
            <Icon className={compact ? "h-5 w-5" : "h-4 w-4"} />
          </span>
          <div className={cn("text-muted-foreground", compact ? "min-h-8 text-[11px] leading-4 md:text-xs" : "truncate text-xs")}>{label}</div>
        </div>
        {trend && (
          <div
            className={cn(
              "flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] shrink-0",
              isUp ? "coach-trend-positive" : "bg-destructive/15 text-destructive"
            )}
          >
            {isUp ? (
              <ArrowUpRight className="h-3 w-3" />
            ) : (
              <ArrowDownRight className="h-3 w-3" />
            )}
            {Math.abs(trend.diff)}
          </div>
        )}
      </div>

      <div className={cn("font-extrabold text-foreground", compact ? "mt-1 flex items-end justify-between text-3xl" : "mt-3 text-3xl")}>
        <span>{value}</span>
        {compact && (
          <span className="coach-kpi-bars flex h-8 items-end gap-1" aria-hidden="true">
            <i className="h-1/3" />
            <i className="h-1/2" />
            <i className="h-3/4" />
            <i className="h-full" />
          </span>
        )}
      </div>

      {hint && (
        <div className="mt-1 text-xs text-muted-foreground truncate">{hint}</div>
      )}

      {!compact && data && data.length > 0 && (
        <div className="mt-3 h-14">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 2, right: 0, left: -28, bottom: 0 }}
            >
              <CartesianGrid
                vertical={false}
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
              />
              <XAxis dataKey="date" hide />
              <YAxis hide />
              <Tooltip
                formatter={(v: number) => [`${v}`, label]}
                labelFormatter={(l) => {
                  try {
                    return format(parseISO(String(l)), "dd/MM");
                  } catch {
                    return String(l);
                  }
                }}
                contentStyle={{
                  fontSize: 12,
                  borderRadius: 12,
                  border: "1px solid hsl(var(--border))",
                  background: "hsl(var(--background) / 0.95)",
                }}
              />
              <defs>
                <linearGradient
                  id={`coach-kpi-grad-${index}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor="hsl(var(--primary))"
                    stopOpacity={0.6}
                  />
                  <stop
                    offset="95%"
                    stopColor="hsl(var(--primary))"
                    stopOpacity={0.05}
                  />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="value"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                fill={`url(#coach-kpi-grad-${index})`}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
